import classnames from 'classnames';
import React, { useEffect, useRef, useState } from 'react';

import * as styles from './table.module.scss';
import { Tooltip } from '../Tooltip/Tooltip';

import ActiveSortLabel from './Sorting/ActiveSortLabel/ActiveSortLabel';
import Arrow from './Sorting/Arrow/Arrow';
import { MultiSort } from './Sorting/MultiSort/MultiSort';
import { SortDirection, SortFieldDisplayProps, SortState } from './Sorting/types';
import { ColumnConfig } from './type';

export interface TableColumnHeaderProps extends ColumnConfig {
  selectedSorting?: SortState;
  defaultSortDirConfig?: SortDirection;
  isSortable: boolean;
  isSticky?: boolean;
  updateSort: (selection: SortState) => void;
  compact?: boolean;
  sortFields?: SortFieldDisplayProps[];
  children?: React.ReactNode[];
}

const TableColumnHeader: React.FC<TableColumnHeaderProps> = (props) => {
  const {
    className,
    headerKey,
    updateSort,
    selectedSorting,
    isSortable,
    isSticky,
    compact,
    sortFields,
    defaultSortDirConfig,
  } = props;

  const [visibleSortDropDown, setVisibleSortDropDown] = useState<string | undefined>(undefined);
  const [headerHeight, setHeaderHeight] = useState<number>(0);
  const trRef = useRef<HTMLTableCellElement>(null);
  const defaultSortDirection = SortDirection.desc;

  const {
    sortDirection: selectedSortDirection,
    sortKey: selectedSortKey,
    headerKey: sortedHeaderKey,
    label: selectedLabel = '',
  } = selectedSorting || {};

  const getNewSortDirection = () => {
    const defaultSortDir = defaultSortDirConfig || defaultSortDirection;
    const resetSortDirection = sortedHeaderKey !== headerKey;
    const sortDirection = selectedSortDirection === SortDirection.desc ? SortDirection.asc : SortDirection.desc;
    return !selectedSortDirection || resetSortDirection ? defaultSortDir : sortDirection;
  };

  useEffect(() => {
    if (trRef && trRef.current) {
      setHeaderHeight(trRef.current.offsetHeight || 0);
    }
  }, [trRef, visibleSortDropDown]);

  const onHeaderColumnClick = () => {
    if (!isSortable) {
      return;
    }
    if (sortFields) {
      setVisibleSortDropDown(headerKey);
      return;
    }
    updateSort({
      headerKey,
      sortKey: headerKey,
      sortDirection: getNewSortDirection(),
      label: '',
    });
  };

  const handleSortFieldClick = (sortingOption: SortFieldDisplayProps, headerKey: string) => {
    setVisibleSortDropDown(undefined);
    updateSort({
      headerKey,
      sortKey: sortingOption.sortKey,
      sortDirection: sortingOption.sortDirection,
      label: sortingOption.label,
    });
  };

  const thClassNames = classnames([
    styles.Table__ColumnHeader,
    { [styles.Table__ColumnHeader__ColumnSortable]: !!isSortable },
    { [styles.Table__ColumnHeader__ColumnSorted]: selectedSorting?.headerKey === headerKey },
    { [styles.Table__HeaderRow__sticky]: isSticky },
    className,
  ]);

  const contentClassNames = classnames(styles.Table__ColumnHeader__content, {
    [styles['Table__ColumnHeader__content--compact']]: compact,
  });

  const labelClassNames = classnames(styles['Table__ColumnHeader__label'], {
    [styles['Table__ColumnHeader__label--compact']]: compact,
  });

  const popupDistanceFromHeader = headerHeight + 4;

  const selectedSortingField =
    headerKey === sortedHeaderKey && sortFields?.find((field) => field.sortKey === selectedSortKey);
  const shouldShowSortArrow = headerKey === sortedHeaderKey;
  const arrowSortDirection = selectedSortDirection || defaultSortDirection;
  return (
    <th ref={trRef} className={thClassNames} data-test-id={`col-${headerKey}`} onClick={onHeaderColumnClick}>
      <div className={contentClassNames}>
        {compact && <Arrow isVisible={shouldShowSortArrow} sortDirection={arrowSortDirection} />}
        <div className={labelClassNames}>
          {props.tooltip ? <Tooltip {...props.tooltip}>{props.name}</Tooltip> : props.name}
          <ActiveSortLabel isVisible={!!selectedSortingField} label={selectedLabel}></ActiveSortLabel>
        </div>
        {!compact && <Arrow isVisible={shouldShowSortArrow} sortDirection={arrowSortDirection} />}
        <MultiSort
          isVisible={!!sortFields && isSortable && visibleSortDropDown === headerKey}
          sortFields={sortFields || []}
          offset={popupDistanceFromHeader}
          onSorted={(sortingOption: SortFieldDisplayProps) => handleSortFieldClick(sortingOption, headerKey)}
          onClickAway={() => setVisibleSortDropDown(undefined)}
          selectedField={visibleSortDropDown === sortedHeaderKey ? selectedSorting : undefined}
        />
      </div>
    </th>
  );
};

export default TableColumnHeader;
